import React, { useState, useEffect, useMemo } from 'react';
import { Users, Plus, Loader2 as Loader, Search, X } from 'lucide-react';
import { projectService } from '../../../services/projectService';
import { projectService as towerProjectService } from '../../services/projectService';
import { verveContactService } from '../../../../../services/VerveContactService';
import { showSuccessMessage, showInfoMessage } from '@/lib/toast';
import { Button } from '@/components/ui/Button';
import { FormField, Textarea } from '@/components/ui/FormComponents';
import { DataTable } from '@/components/ui/DataTable';

const PM_COLUMNS = [
   {
      header: 'Project Manager',
      key: 'projectManager',
      render: (row) => (
         <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-blue-50 text-[#2b448f] font-semibold text-[12px] flex items-center justify-center border border-blue-100">
               {row.getInitials(row.pm.name || `${row.pm.firstName || ''} ${row.pm.lastName || ''}`.trim() || 'M')}
            </div>
            <span className="text-[13px] font-medium text-slate-700 font-['Poppins']">
               {row.pm.name || `${row.pm.firstName || ''} ${row.pm.lastName || ''}`.trim() || 'Manager'}
            </span>
         </div>
      )
   },
   {
      header: 'Contact Details',
      key: 'contactDetails',
      render: (row) => (
         <div className="flex flex-col">
            <span className="text-[12px] font-medium text-slate-600">{row.pm.email || '—'}</span>
            <span className="text-[12px] font-medium text-slate-600">{row.pm.phone || '—'}</span>
         </div>
      )
   },
   {
      header: 'Assignment Notes',
      key: 'assignmentNotes',
      className: 'max-w-[300px]',
      render: (row) => (
         <p className="text-[12px] font-medium text-slate-500 leading-relaxed italic truncate">
            {row.pmAssignmentNotes || 'No notes provided.'}
         </p>
      )
   }
];

export const ProjectManagerAssignmentForm = ({ project, onUpdate, onClose }) => {
   const actualProject = project?.data?.data || project?.data || project;
   const [managers, setManagers] = useState([]);
   
   const [selectedManagers, setSelectedManagers] = useState(() => {
      if (!actualProject?.projectManager) return [];
      if (Array.isArray(actualProject.projectManager)) {
         return actualProject.projectManager.map(m => m._id || m);
      }
      return [actualProject.projectManager._id || actualProject.projectManager];
   });
   const [notes, setNotes] = useState(actualProject.pmAssignmentNotes || '');
   const [loading, setLoading] = useState(false);
   const [search, setSearch] = useState('');

   useEffect(() => {
      const fetchManagers = async () => {
         try {
            // Need to fetch users who are managers. Using towerProjectService since it might have getProjectManagers
            const response = await towerProjectService.getProjectManagers();
            const rawManagers = Array.isArray(response) ? response : (response?.data?.data || response?.data || []);
            const activeManagers = rawManagers.filter((u) => u?.status !== 'Inactive');
            setManagers(activeManagers);
         } catch (err) {
            console.error(err);
         }
      };
      fetchManagers();
   }, []);

   const toggleManager = (id) => {
      setSelectedManagers(prev => 
         prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
      );
   };

   const filteredManagers = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return managers;
      return managers.filter(
         (m) =>
            (m.name || '').toLowerCase().includes(q) ||
            (m.phone || '').toLowerCase().includes(q)
      );
   }, [managers, search]);

   const handleAssign = async () => {
      if (!selectedManagers.length) return;

      setLoading(true);
      try {
         const actualProject = project?.data?.data || project?.data || project;
         const projectId = actualProject?._id || actualProject?.id || actualProject?.customId || actualProject?.projectId || actualProject?.verveId;
         
         if (!projectId) {
            console.error("Project object structure:", project);
            throw new Error("Project ID is missing from the project object.");
         }

         // Using verveContactService to hit /verve-contacts endpoint
         await verveContactService.updateContact(projectId, { 
            projectManager: selectedManagers,
            pmAssignmentNotes: notes,
         });
         
         // Re-fetch project to get updated data
         const res = await towerProjectService.getProjectById(projectId);
         const updatedProject = res?.data?.data || res?.data || res;
         
         if (onUpdate && updatedProject) onUpdate(updatedProject);
         if (onClose) onClose();
         showSuccessMessage("Project Manager assigned successfully");
      } catch (error) {
         console.error(error);
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="flex flex-col gap-4">

         {/* Selected count badge — always visible, starts at 0 */}
         <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md transition-all">
            <span className={`w-5 h-5 rounded-full text-white text-[10px] font-black flex items-center justify-center transition-colors ${
               selectedManagers.length > 0 ? 'bg-[#64748b]' : 'bg-slate-300'
            }`}>
               {selectedManagers.length}
            </span>
            <span className="text-[12px] font-semibold text-slate-600">
               {selectedManagers.length === 0
                  ? '0 managers selected'
                  : selectedManagers.length === 1
                  ? '1 manager selected'
                  : `${selectedManagers.length} managers selected`}
            </span>
         </div>

         {/* Multi-select checkbox list */}
         <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-slate-500">Select Project Manager</label>

            {/* Search input */}
            <div className="relative">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
               <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or phone..."
                  className="w-full h-[35px] pl-8 pr-8 text-[12px] border border-slate-200 rounded-md text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:border-[#64748b] focus:ring-[#64748b]/20 transition-all bg-white"
               />
               {search && (
                  <button
                     type="button"
                     onClick={() => setSearch('')}
                     className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                     <X className="w-3.5 h-3.5" />
                  </button>
               )}
            </div>

            <div className="flex flex-col border border-slate-200 rounded-md overflow-hidden max-h-[189px] overflow-y-auto">
               {managers.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[12px] text-slate-400 italic">
                     No project managers found.
                  </div>
               ) : filteredManagers.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[12px] text-slate-400 italic">
                     No results for &ldquo;{search}&rdquo;
                  </div>
               ) : (
                  filteredManagers.map((m, idx) => {
                     const isChecked = selectedManagers.includes(m._id);
                     return (
                        <label
                           key={m._id}
                           htmlFor={`pm-check-${m._id}`}
                           className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all select-none
                              ${idx !== filteredManagers.length - 1 ? 'border-b border-slate-100' : ''}
                              ${isChecked ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/60'}
                           `}
                        >
                           {/* Hidden real checkbox */}
                           <input
                              id={`pm-check-${m._id}`}
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleManager(m._id)}
                              className="sr-only"
                           />
                           {/* Custom styled checkbox box */}
                           <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all
                              ${isChecked ? 'bg-[#64748b] border-[#64748b]' : 'bg-white border-slate-300 hover:border-[#64748b]'}`}
                           >
                              {isChecked && (
                                 <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
                                    <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                 </svg>
                              )}
                           </div>

                           {/* Avatar initials */}
                           <div className={`w-7 h-7 rounded flex items-center justify-center text-[11px] font-bold flex-shrink-0 transition-all
                              ${isChecked ? 'bg-[#64748b] text-white' : 'bg-slate-100 text-slate-500'}`}
                           >
                              {(m.name || '?').charAt(0).toUpperCase()}
                           </div>

                           {/* Name & phone */}
                           <div className="flex flex-col min-w-0 flex-1">
                              <span className={`text-[13px] font-semibold truncate transition-colors
                                 ${isChecked ? 'text-slate-800' : 'text-slate-600'}`}>
                                 {m.name}
                              </span>
                              {m.phone && (
                                 <span className="text-[11px] text-slate-400 font-medium">{m.phone}</span>
                              )}
                           </div>

                           {/* Selected pill */}
                           {isChecked && (
                              <span className="text-[10px] font-bold text-[#64748b] bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                 Selected
                              </span>
                           )}
                        </label>
                     );
                  })
               )}
            </div>
         </div>

         {/* Assignment note */}
         <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-slate-500">Assignment Note (Optional)</label>
            <textarea
               value={notes}
               onChange={(e) => setNotes(e.target.value)}
               placeholder="Add internal instructions or context for the PM..."
               className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md text-[12px] font-medium text-slate-600 focus:outline-none focus:ring-1 focus:border-[#64748b] focus:ring-[#64748b]/20 min-h-[72px] resize-none transition-all"
            />
         </div>

         {/* Action buttons using Button component variants */}
         <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100">
            <Button
               variant="secondary"
               onClick={onClose}
               className="w-full sm:flex-1"
            >
               Cancel
            </Button>
            <Button
               variant="orange"
               onClick={handleAssign}
               disabled={!selectedManagers.length || loading}
               loading={loading}
               className="w-full sm:flex-1"
            >
               {loading ? '' : 'Confirm Assignment'}
            </Button>
         </div>
      </div>
   );
};

export const ProjectManagerAssignment = ({ project, onUpdate, getInitials, onAssignClick }) => {
   const [managers, setManagers] = useState([]);

   useEffect(() => {
      const fetchManagers = async () => {
         try {
            const response = await towerProjectService.getProjectManagers();
            const rawManagers = Array.isArray(response) ? response : (response?.data?.data || response?.data || []);
            const activeManagers = rawManagers.filter((u) => u?.status !== 'Inactive');
            setManagers(activeManagers);
            
            // Auto-assign if exactly one manager exists and none is assigned
            const isUnassigned = !project?.projectManager || (Array.isArray(project.projectManager) && project.projectManager.length === 0);
            if (isUnassigned && activeManagers.length === 1) {
               const onlyManager = activeManagers[0];
               await verveContactService.updateContact(project._id, { projectManager: [onlyManager._id] });
               if (onUpdate) onUpdate();
            }
         } catch (err) {
            console.error(err);
         }
      };
      fetchManagers();
   }, [project?._id, project?.projectManager]);
   const actualProject = project?.data?.data || project?.data || project;

   console.log("PROJECT MANAGER DATA:", actualProject?.projectManager);

   const tableData = useMemo(() => {
      if (!actualProject?.projectManager) return [];
      const pms = Array.isArray(actualProject.projectManager) ? actualProject.projectManager : [actualProject.projectManager];
      // Filter out empty or invalid PMs
      const validPms = pms.filter(pm => pm && (typeof pm === 'string' ? pm.length > 0 : pm._id));
      if (validPms.length === 0) return [];
      
      return validPms.map(pm => ({
         pm: typeof pm === 'string' ? { _id: pm, name: 'Unknown Manager', email: '', phone: '' } : pm,
         pmAssignmentNotes: actualProject.pmAssignmentNotes,
         getInitials
      }));
   }, [actualProject?.projectManager, actualProject?.pmAssignmentNotes, getInitials]);

   return (
      <div className="bg-white border border-slate-100 rounded-xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom duration-500">
         <div className="p-4 border-b border-slate-100 flex items-center justify-end">
            <button 
               onClick={onAssignClick} 
               className="flex items-center gap-2 px-4 py-2 bg-transparent border border-[#2b448f] cursor-pointer text-[#2b448f] rounded-md text-[10px] font-semibold tracking-wider hover:bg-blue-50 transition-all shadow-sm"
            >
               <Plus className="w-3.5 h-3.5" />
               {(!actualProject?.projectManager || (Array.isArray(actualProject.projectManager) && actualProject.projectManager.length === 0)) ? 'Assign PM' : 'Update PM'}
            </button>
         </div>

         <DataTable
            columns={PM_COLUMNS}
            data={tableData}
            noScroll={true}
         />
      </div>
   );
};
